import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
interface ProfilePreviewProps {
  role: 'brand' | 'organizer';
  basics: {
    name: string;
    type: string;
  };
  onNext: () => void;
}
export function ProfilePreview({
  role,
  basics,
  onNext
}: ProfilePreviewProps) {
  const accentColor = role === 'brand' ? 'text-cyan-400' : 'text-purple-400';
  const bgGradient = role === 'brand' ? 'from-cyan-500/20' : 'from-purple-500/20';
  const buttonColor = role === 'brand' ? 'bg-cyan-500 hover:bg-cyan-400 text-slate-900' : 'bg-purple-600 hover:bg-purple-500 text-white';
  const shadowColor = role === 'brand' ? 'rgba(6,182,212,0.3)' : 'rgba(168,85,247,0.3)';
  return <div className="max-w-4xl mx-auto" data-id="element-894">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-center mb-12" data-id="element-895">
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800 border border-slate-700 mb-6 ${accentColor}`} data-id="element-896">
          <Sparkles size={16} data-id="element-897" />
          <span className="text-sm font-medium" data-id="element-898">Your Future Profile</span>
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-899">
          Imagine {basics.name} on SponsrAI
        </h1>
        <p className="text-xl text-slate-400 max-w-2xl mx-auto" data-id="element-900">
          Here's a preview of how you'll look to potential partners. Let's make
          this a reality.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-8 items-center mb-12" data-id="element-901">
        {/* Profile Card Preview */}
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.2
      }} className="bg-slate-800 border border-slate-700 rounded-2xl p-8 relative overflow-hidden" data-id="element-902">
          <div className={`absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl ${bgGradient} to-transparent rounded-bl-full opacity-50 pointer-events-none`} data-id="element-903" />

          <div className="relative z-10" data-id="element-904">
            <div className="flex items-center gap-4 mb-6" data-id="element-905">
              <div className="w-16 h-16 rounded-xl bg-slate-700 flex items-center justify-center text-2xl font-bold text-slate-500" data-id="element-906">
                {basics.name[0]}
              </div>
              <div data-id="element-907">
                <h3 className="text-2xl font-bold text-white" data-id="element-908">{basics.name}</h3>
                <p className={`text-sm font-medium ${accentColor}`} data-id="element-909">
                  {basics.type}
                </p>
              </div>
            </div>

            <div className="space-y-4 mb-8" data-id="element-910">
              <div className="h-4 bg-slate-700/50 rounded w-3/4" data-id="element-911"></div>
              <div className="h-4 bg-slate-700/50 rounded w-full" data-id="element-912"></div>
              <div className="h-4 bg-slate-700/50 rounded w-5/6" data-id="element-913"></div>
            </div>

            <div className="flex gap-2" data-id="element-914">
              <span className="px-3 py-1 rounded-full bg-slate-700/50 text-slate-400 text-xs" data-id="element-915">
                Sustainability
              </span>
              <span className="px-3 py-1 rounded-full bg-slate-700/50 text-slate-400 text-xs" data-id="element-916">
                Innovation
              </span>
              <span className="px-3 py-1 rounded-full bg-slate-700/50 text-slate-400 text-xs" data-id="element-917">
                Tech
              </span>
            </div>
          </div>
        </motion.div>

        {/* Matches Preview */}
        <motion.div initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.4
      }} className="relative" data-id="element-918">
          <div className="absolute -inset-4 bg-gradient-to-r from-slate-900 to-transparent z-10" data-id="element-919"></div>
          <div className="space-y-4 opacity-50 blur-[1px]" data-id="element-920">
            {[1, 2, 3].map(i => <div key={i} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 flex items-center gap-4" data-id="element-921">
                <div className="w-12 h-12 rounded-lg bg-slate-700/50" data-id="element-922"></div>
                <div className="flex-1" data-id="element-923">
                  <div className="h-4 bg-slate-700/50 rounded w-1/2 mb-2" data-id="element-924"></div>
                  <div className="h-3 bg-slate-700/30 rounded w-1/3" data-id="element-925"></div>
                </div>
                <div className="w-12 h-12 rounded-full border-4 border-green-500/20 flex items-center justify-center text-xs font-bold text-green-500" data-id="element-926">
                  9{i}%
                </div>
              </div>)}
          </div>

          <div className="absolute inset-0 flex items-center justify-center z-20" data-id="element-927">
            <div className="text-center" data-id="element-928">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-800 border border-slate-700 mb-4 shadow-xl" data-id="element-929">
                <Sparkles className={accentColor} size={32} data-id="element-930" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-931">
                3 Perfect Matches Found
              </h3>
              <p className="text-slate-400 text-sm" data-id="element-932">
                Complete your profile to connect
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.6
    }} className="text-center" data-id="element-933">
        <button onClick={onNext} className={`px-10 py-4 rounded-full font-bold text-lg transition-all shadow-[0_0_30px_${shadowColor}] flex items-center gap-2 mx-auto ${buttonColor}`} data-id="element-934">
          Build My Profile <ArrowRight size={20} data-id="element-935" />
        </button>
        <p className="mt-4 text-slate-500 text-sm" data-id="element-936">Takes about 2 minutes</p>
      </motion.div>
    </div>;
}